import { PAYMENT_METHODS } from "@/common/static/checkout/payment.static";
import { cn } from "@/common/utils/css.utils";
import Image from "@/components/common/Image";

type PaymentMethodsProps = {
  size?: "small" | "large";
  /**
   * `wrap` (the default) is the original single flowing line, used by the
   * Footer and everywhere else. `grid` is opt-in and lays the logos out on a
   * fixed five-column grid, each logo stretching to its cell, so a tall
   * container is filled rather than leaving a band of white space under one
   * wrapped line. `PAYMENT_METHODS` currently holds exactly ten entries, so
   * five columns give two full rows with no ragged remainder.
   */
  layout?: "wrap" | "grid";
};

const SIZE_MAP: Record<NonNullable<PaymentMethodsProps["size"]>, string> = {
  small: "h-4 w-7",
  large: "h-7 w-11",
};

/**
 * In grid mode the cell defines the width, so the logos scale with the
 * container instead of overflowing a narrow card. Only the height varies.
 */
const GRID_SIZE_MAP: Record<NonNullable<PaymentMethodsProps["size"]>, string> =
  {
    small: "h-7 w-full",
    large: "h-10 w-full",
  };

export const PaymentMethods = ({
  size = "small",
  layout = "wrap",
}: PaymentMethodsProps) => {
  const isGrid = layout === "grid";

  return (
    <div
      className={cn(
        isGrid
          ? "grid grid-cols-5 gap-x-3 gap-y-4"
          : "-ml-2 -mt-2 flex flex-wrap space-x-2 space-y-2 [&>:first-child]:ml-2 [&>:first-child]:mt-2"
      )}
    >
      {PAYMENT_METHODS.map(method => (
        <div
          key={method.name}
          className={cn(
            "relative",
            isGrid ? GRID_SIZE_MAP[size] : SIZE_MAP[size]
          )}
        >
          <Image
            src={method.image}
            alt={method.name}
            layout="fill"
            objectFit="contain"
          />
        </div>
      ))}
    </div>
  );
};
